import { useFrame } from '@react-three/fiber';
import type { AtmosphereApi } from '@takram/three-atmosphere/r3f';
import type { CloudsEffect } from '@takram/three-clouds';
import {
  useEffect,
  useMemo,
  useRef,
  type FC,
  type MutableRefObject,
} from 'react';
import { MathUtils, Matrix4, Vector3 } from 'three';
import { skyState } from '../skyStore';
import { SunLensFlareEffect } from './sunLensFlareEffect';

export const SunLensFlare: FC<{
  atmosphereRef: MutableRefObject<AtmosphereApi | null>;
  cloudsRef: MutableRefObject<CloudsEffect | null>;
}> = ({ atmosphereRef, cloudsRef }) => {
  const effect = useMemo(
    () =>
      new SunLensFlareEffect(() =>
        skyState.cloudsEnabled
          ? (cloudsRef.current?.atmosphereOverlay?.map ?? null)
          : null
      ),
    [cloudsRef]
  );
  const vectors = useMemo(
    () => ({
      inverse: new Matrix4(),
      sun: new Vector3(),
      view: new Vector3(),
      position: new Vector3(),
    }),
    []
  );
  const visibility = useRef(0);
  useEffect(() => () => effect.dispose(), [effect]);

  useFrame(({ camera, gl }, delta) => {
    const atmosphere = atmosphereRef.current;
    if (!atmosphere) {
      effect.visibility = 0;
      return;
    }
    vectors.inverse.copy(atmosphere.worldToECEFMatrix).invert();
    vectors.sun
      .copy(atmosphere.sunDirection)
      .transformDirection(vectors.inverse);
    vectors.view
      .copy(vectors.sun)
      .transformDirection(camera.matrixWorldInverse);
    let target = 0;
    if (
      skyState.postEnabled &&
      skyState.post.lensFlare &&
      vectors.view.z < -0.01
    ) {
      camera.getWorldPosition(vectors.position);
      vectors.position.addScaledVector(vectors.sun, 1000).project(camera);
      effect.source.copy(vectors.position);
      const edge = Math.max(
        Math.abs(vectors.position.x),
        Math.abs(vectors.position.y)
      );
      target =
        (1 - MathUtils.smoothstep(edge, 0.8, 1)) *
        MathUtils.smoothstep(vectors.sun.y, 0, 0.08);
    }
    visibility.current = MathUtils.damp(
      visibility.current,
      target,
      10,
      Math.min(delta, 0.1)
    );
    effect.setAppearance(skyState.post);
    effect.visibility = visibility.current * skyState.post.lensFlareIntensity;
    gl.getDrawingBufferSize(effect.resolution);
  });

  return <primitive object={effect} dispose={null} />;
};
